/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable sonarjs/no-duplicate-string */
import * as React from "react";

import { EmailContentType, getDatePlusNHours } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { CommunicationFormData, useFormCommunicationData } from "../../../hooks/useFormCommunicationData";

import { DateEmailScheduler } from "@/components/EmailSchedulerDialog/DateEmailScheduler";
import { DurationEmailScheduler } from "@/components/EmailSchedulerDialog/DurationEmailScheduler";
import { AmplitudeEvents } from "@/constants/amplitude";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/hooks/useTranslate";

interface BaseEmailSchedulerProps {
  formId: string;
  emailContentId: string;
  isOpen: boolean;
  closeEmailScheduler: (success: boolean) => Promise<void>;
  handleScheduleSuccess: () => Promise<void>;
  handleScheduleError: () => Promise<void>;
  handleUnscheduleSuccess: () => Promise<void>;
  handleUnscheduleError: () => Promise<void>;
  logScheduleEvent: (params: Record<string, unknown>) => void;
  logUnscheduleEvent: (params: Record<string, unknown>) => void;
}

interface DateBasedSchedulerProps extends BaseEmailSchedulerProps {
  scheduledFor?: string;
  maxDate?: Date;
  minDate?: Date;
  emailType?: EmailContentType | null;
}

interface DurationBasedSchedulerProps extends BaseEmailSchedulerProps {
  formData: CommunicationFormData | undefined;
}

export const EmailWithFormSchedulerWrapper = ({
  formId,
  emailType,
  closeEmailScheduler,
  isSchedulerOpen,
  emailContentId,
  refetch,
  minScheduleDate,
}: {
  formId?: string;
  emailType: EmailContentType;
  emailContentId: string;
  refetch: () => void;
  closeEmailScheduler: (success: boolean) => Promise<void>;
  isSchedulerOpen: boolean;
  minScheduleDate?: Date;
}) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const { data: formData } = useFormCommunicationData(formId);
  const { data: emailContentData, refetch: refetchEmail } = trpc.getCommunicationEmail.useQuery(
    { emailContentId },
    { enabled: Boolean(emailContentId) }
  );

  const eventLastStartDate = React.useMemo(() => {
    if (!formData?.occurrences?.length) {
      return undefined;
    }
    const dates = formData.occurrences.map((occurrence) => new Date(occurrence.startUtc).getTime());
    return new Date(Math.max(...dates));
  }, [formData]);

  if (!formData || !emailContentData) {
    return null;
  }

  const handleRefetch = async () => {
    await refetchEmail();
    refetch();
  };

  const handleScheduleSuccess = async () => {
    handleRefetch();
    await closeEmailScheduler(true);
    enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.schedule.success"), {
      vibe: "positive",
    });
  };

  const handleScheduleError = async () => {
    await closeEmailScheduler(false);
    enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.schedule.error"), {
      vibe: "danger",
    });
  };

  const handleUnscheduleSuccess = async () => {
    handleRefetch();
    await closeEmailScheduler(true);
    enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.unschedule.success"), {
      vibe: "positive",
    });
  };

  const handleUnscheduleError = async () => {
    await closeEmailScheduler(false);
    enqueueSnackbar(t("dashboard", "EmailEditor.scheduler.unschedule.error"), {
      vibe: "danger",
    });
  };

  const logScheduleEvent = (params: Record<string, unknown>) => {
    logAmplitudeEvent(AmplitudeEvents.EmailScheduleForLaterCompleted, {
      emailType,
      formId,
      ...params,
    });
  };

  const logUnscheduleEvent = (params: Record<string, unknown> = {}) => {
    logAmplitudeEvent(AmplitudeEvents.EmailUnscheduled, {
      emailType,
      formId,
      ...params,
    });
  };

  if (!formId) {
    return null;
  }

  switch (emailType) {
    case EmailContentType.BLAST:
      return (
        <BlastEmailScheduler
          formId={formId}
          emailContentId={emailContentId}
          scheduledFor={emailContentData.scheduledFor}
          isOpen={isSchedulerOpen}
          closeEmailScheduler={closeEmailScheduler}
          handleScheduleSuccess={handleScheduleSuccess}
          handleScheduleError={handleScheduleError}
          handleUnscheduleSuccess={handleUnscheduleSuccess}
          handleUnscheduleError={handleUnscheduleError}
          logScheduleEvent={logScheduleEvent}
          logUnscheduleEvent={logUnscheduleEvent}
        />
      );

    case EmailContentType.INVITATION:
      return (
        <InvitationEmailScheduler
          formId={formId}
          emailContentId={emailContentId}
          scheduledFor={emailContentData.scheduledFor}
          maxDate={eventLastStartDate}
          isOpen={isSchedulerOpen}
          closeEmailScheduler={closeEmailScheduler}
          handleScheduleSuccess={handleScheduleSuccess}
          handleScheduleError={handleScheduleError}
          handleUnscheduleSuccess={handleUnscheduleSuccess}
          handleUnscheduleError={handleUnscheduleError}
          logScheduleEvent={logScheduleEvent}
          logUnscheduleEvent={logUnscheduleEvent}
        />
      );

    case EmailContentType.FOLLOW_UP:
      return (
        <ScheduledForDateEmailScheduler
          formId={formId}
          emailContentId={emailContentId}
          scheduledFor={emailContentData.scheduledFor}
          maxDate={eventLastStartDate}
          isOpen={isSchedulerOpen}
          closeEmailScheduler={closeEmailScheduler}
          handleScheduleSuccess={handleScheduleSuccess}
          handleScheduleError={handleScheduleError}
          handleUnscheduleSuccess={handleUnscheduleSuccess}
          handleUnscheduleError={handleUnscheduleError}
          logScheduleEvent={logScheduleEvent}
          logUnscheduleEvent={logUnscheduleEvent}
          minDate={minScheduleDate}
          emailType={EmailContentType.FOLLOW_UP}
        />
      );

    case EmailContentType.REMINDER:
      return (
        <ReminderEmailScheduler
          formId={formId}
          formData={formData}
          emailContentId={emailContentId}
          isOpen={isSchedulerOpen}
          closeEmailScheduler={closeEmailScheduler}
          handleScheduleSuccess={handleScheduleSuccess}
          handleScheduleError={handleScheduleError}
          handleUnscheduleSuccess={handleUnscheduleSuccess}
          handleUnscheduleError={handleUnscheduleError}
          logScheduleEvent={logScheduleEvent}
          logUnscheduleEvent={logUnscheduleEvent}
        />
      );

    case EmailContentType.POST_EVENT:
      return (
        <PostEventEmailScheduler
          formId={formId}
          formData={formData}
          emailContentId={emailContentId}
          isOpen={isSchedulerOpen}
          closeEmailScheduler={closeEmailScheduler}
          handleScheduleSuccess={handleScheduleSuccess}
          handleScheduleError={handleScheduleError}
          handleUnscheduleSuccess={handleUnscheduleSuccess}
          handleUnscheduleError={handleUnscheduleError}
          logScheduleEvent={logScheduleEvent}
          logUnscheduleEvent={logUnscheduleEvent}
        />
      );

    default:
      if (emailContentData.scheduledFor) {
        return (
          <ScheduledForDateEmailScheduler
            formId={formId}
            emailContentId={emailContentId}
            scheduledFor={emailContentData.scheduledFor}
            maxDate={eventLastStartDate}
            isOpen={isSchedulerOpen}
            closeEmailScheduler={closeEmailScheduler}
            handleScheduleSuccess={handleScheduleSuccess}
            handleScheduleError={handleScheduleError}
            handleUnscheduleSuccess={handleUnscheduleSuccess}
            handleUnscheduleError={handleUnscheduleError}
            logScheduleEvent={logScheduleEvent}
            logUnscheduleEvent={logUnscheduleEvent}
            minDate={minScheduleDate}
            emailType={null}
          />
        );
      }
      return null;
  }
};

const BlastEmailScheduler = ({
  emailContentId,
  scheduledFor,
  isOpen,
  closeEmailScheduler,
  handleScheduleSuccess,
  handleScheduleError,
  handleUnscheduleSuccess,
  handleUnscheduleError,
  logScheduleEvent,
  logUnscheduleEvent,
}: DateBasedSchedulerProps) => {
  const { t } = useTranslate();
  const { mutateAsync: scheduleEmailContentMutation } = trpc.scheduleEmailContent.useMutation();
  const { mutateAsync: unscheduleEmailContentMutation } = trpc.unscheduleEmailContent.useMutation();

  const scheduledDateTime = React.useMemo(() => (scheduledFor ? new Date(scheduledFor) : undefined), [scheduledFor]);

  const handleSchedulerSubmit = React.useCallback(
    async (scheduledFor: Date) => {
      const { error } = await scheduleEmailContentMutation({
        emailContentId,
        scheduledFor,
      });

      if (error) {
        await handleScheduleError();
      } else {
        await handleScheduleSuccess();
        logScheduleEvent({
          emailContentId,
          scheduledFor,
        });
      }
    },
    [scheduleEmailContentMutation, emailContentId, handleScheduleError, handleScheduleSuccess, logScheduleEvent]
  );

  const handleSchedulerCancel = React.useCallback(async () => {
    if (scheduledDateTime) {
      const { error } = await unscheduleEmailContentMutation({
        emailContentId,
      });
      if (error) {
        await handleUnscheduleError();
      } else {
        await handleUnscheduleSuccess();
        logUnscheduleEvent({ emailContentId });
      }
    } else {
      // Close the scheduler when cancelling a new (not yet scheduled) email
      await closeEmailScheduler(false);
    }
  }, [
    emailContentId,
    scheduledDateTime,
    unscheduleEmailContentMutation,
    logUnscheduleEvent,
    handleUnscheduleSuccess,
    handleUnscheduleError,
    closeEmailScheduler,
  ]);

  return (
    <DateEmailScheduler
      isOpen={isOpen}
      title={t("dashboard", "EmailEditor.scheduler.title.blast")}
      scheduledFor={scheduledDateTime || getDatePlusNHours(new Date(), 1)}
      cancelLabel={
        scheduledDateTime ? t("dashboard", "EmailScheduler.unschedule") : t("dashboard", "EmailScheduler.cancel")
      }
      onSubmit={handleSchedulerSubmit}
      onCancel={handleSchedulerCancel}
      onClose={() => closeEmailScheduler(false)}
    />
  );
};

const InvitationEmailScheduler = ({
  emailContentId,
  scheduledFor,
  maxDate,
  isOpen,
  closeEmailScheduler,
  handleScheduleSuccess,
  handleScheduleError,
  handleUnscheduleSuccess,
  handleUnscheduleError,
  logScheduleEvent,
  logUnscheduleEvent,
}: DateBasedSchedulerProps) => {
  const { t } = useTranslate();
  const { mutateAsync: scheduleEmailContentMutation } = trpc.scheduleEmailContent.useMutation();
  const { mutateAsync: unscheduleEmailContentMutation } = trpc.unscheduleEmailContent.useMutation();

  const scheduledDateTime = React.useMemo(() => (scheduledFor ? new Date(scheduledFor) : undefined), [scheduledFor]);

  const handleSchedulerSubmit = React.useCallback(
    async (scheduledFor: Date) => {
      const { error } = await scheduleEmailContentMutation({
        emailContentId,
        scheduledFor,
      });

      if (error) {
        await handleScheduleError();
      } else {
        await handleScheduleSuccess();
        logScheduleEvent({
          emailContentId,
          scheduledFor,
        });
      }
    },
    [scheduleEmailContentMutation, emailContentId, handleScheduleError, handleScheduleSuccess, logScheduleEvent]
  );

  const handleSchedulerCancel = React.useCallback(async () => {
    if (scheduledDateTime) {
      const { error } = await unscheduleEmailContentMutation({
        emailContentId,
      });
      if (error) {
        await handleUnscheduleError();
      } else {
        await handleUnscheduleSuccess();
        logUnscheduleEvent({ emailContentId });
      }
    } else {
      // Close the scheduler when cancelling a new (not yet scheduled) email
      await closeEmailScheduler(false);
    }
  }, [
    emailContentId,
    scheduledDateTime,
    unscheduleEmailContentMutation,
    logUnscheduleEvent,
    handleUnscheduleSuccess,
    handleUnscheduleError,
    closeEmailScheduler,
  ]);

  return (
    <DateEmailScheduler
      isOpen={isOpen}
      title={t("dashboard", "EmailEditor.scheduler.title.invitation")}
      scheduledFor={scheduledDateTime || getDatePlusNHours(new Date(), 1)}
      maxDate={maxDate}
      cancelLabel={
        scheduledDateTime ? t("dashboard", "EmailScheduler.unschedule") : t("dashboard", "EmailScheduler.cancel")
      }
      onSubmit={handleSchedulerSubmit}
      onCancel={handleSchedulerCancel}
      onClose={() => closeEmailScheduler(false)}
    />
  );
};

const ScheduledForDateEmailScheduler = ({
  emailContentId,
  scheduledFor,
  maxDate,
  isOpen,
  closeEmailScheduler,
  handleScheduleSuccess,
  handleScheduleError,
  handleUnscheduleSuccess,
  handleUnscheduleError,
  logScheduleEvent,
  logUnscheduleEvent,
  minDate,
  emailType = EmailContentType.FOLLOW_UP,
}: DateBasedSchedulerProps) => {
  const { t } = useTranslate();
  const { mutateAsync: scheduleEmailContentMutation } = trpc.scheduleEmailContent.useMutation();
  const { mutateAsync: unscheduleEmailContentMutation } = trpc.unscheduleEmailContent.useMutation();

  const scheduledDateTime = React.useMemo(() => (scheduledFor ? new Date(scheduledFor) : undefined), [scheduledFor]);

  const handleSchedulerSubmit = React.useCallback(
    async (scheduledFor: Date) => {
      const { error } = await scheduleEmailContentMutation({
        emailContentId,
        scheduledFor,
      });

      if (error) {
        await handleScheduleError();
      } else {
        await handleScheduleSuccess();
        logScheduleEvent({
          emailContentId,
          scheduledFor,
        });
      }
    },
    [scheduleEmailContentMutation, emailContentId, handleScheduleError, handleScheduleSuccess, logScheduleEvent]
  );

  const handleSchedulerCancel = React.useCallback(async () => {
    if (scheduledDateTime) {
      const { error } = await unscheduleEmailContentMutation({
        emailContentId,
      });
      if (error) {
        await handleUnscheduleError();
      } else {
        await handleUnscheduleSuccess();
        logUnscheduleEvent({ emailContentId });
      }
    } else {
      // Close the scheduler when cancelling a new (not yet scheduled) email
      await closeEmailScheduler(false);
    }
  }, [
    emailContentId,
    scheduledDateTime,
    unscheduleEmailContentMutation,
    logUnscheduleEvent,
    handleUnscheduleSuccess,
    handleUnscheduleError,
    closeEmailScheduler,
  ]);

  const defaultScheduleDate = minDate && minDate > new Date() ? minDate : getDatePlusNHours(new Date(), 1);

  return (
    <DateEmailScheduler
      isOpen={isOpen}
      title={t(
        "dashboard",
        emailType === EmailContentType.FOLLOW_UP
          ? "EmailEditor.scheduler.title.followup"
          : "EmailEditor.scheduler.title.basic"
      )}
      scheduledFor={scheduledDateTime || defaultScheduleDate}
      maxDate={maxDate}
      cancelLabel={
        scheduledDateTime ? t("dashboard", "EmailScheduler.unschedule") : t("dashboard", "EmailScheduler.cancel")
      }
      onSubmit={handleSchedulerSubmit}
      onCancel={handleSchedulerCancel}
      onClose={() => closeEmailScheduler(false)}
      minDate={minDate}
    />
  );
};

const ReminderEmailScheduler = ({
  formId,
  formData,
  emailContentId,
  isOpen,
  closeEmailScheduler,
  handleScheduleSuccess,
  handleScheduleError,
  handleUnscheduleSuccess,
  handleUnscheduleError,
  logScheduleEvent,
  logUnscheduleEvent,
}: DurationBasedSchedulerProps) => {
  const { t } = useTranslate();
  const { mutateAsync: scheduleReminderEmail } = trpc.scheduleEventReminder.useMutation();
  const { mutateAsync: unscheduleReminderEmail } = trpc.unscheduleEventReminder.useMutation();

  const currentOffset = formData?.reminderSendDateOffset;

  const handleSubmit = React.useCallback(
    async (inDays: number) => {
      const { error } = await scheduleReminderEmail({ ticketingId: formId, offset: inDays });
      if (error) {
        await handleScheduleError();
      } else {
        logScheduleEvent({ offsetInDays: inDays, emailContentId });
        await handleScheduleSuccess();
      }
    },
    [formId, scheduleReminderEmail, emailContentId, logScheduleEvent, handleScheduleSuccess, handleScheduleError]
  );

  const handleCancel = React.useCallback(async () => {
    if (currentOffset) {
      const { error } = await unscheduleReminderEmail({ ticketingId: formId });
      if (error) {
        await handleUnscheduleError();
      } else {
        logUnscheduleEvent({ emailContentId });
        await handleUnscheduleSuccess();
      }
    } else {
      // Close the scheduler when cancelling a new (not yet scheduled) email
      await closeEmailScheduler(false);
    }
  }, [
    currentOffset,
    emailContentId,
    formId,
    unscheduleReminderEmail,
    logUnscheduleEvent,
    handleUnscheduleSuccess,
    handleUnscheduleError,
    closeEmailScheduler,
  ]);

  return (
    <DurationEmailScheduler
      isOpen={isOpen}
      title={t("dashboard", "EmailEditor.scheduler.title.reminder")}
      label={t("dashboard", "EmailEditor.scheduler.label.reminder")}
      scheduleInDays={currentOffset ?? 7}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onClose={async () => await closeEmailScheduler(false)}
      cancelLabel={
        currentOffset ? t("dashboard", "EmailScheduler.unschedule") : t("dashboard", "EmailScheduler.cancel")
      }
    />
  );
};

const PostEventEmailScheduler = ({
  formId,
  formData,
  emailContentId,
  isOpen,
  closeEmailScheduler,
  handleScheduleSuccess,
  handleScheduleError,
  handleUnscheduleSuccess,
  handleUnscheduleError,
  logScheduleEvent,
  logUnscheduleEvent,
}: DurationBasedSchedulerProps) => {
  const { t } = useTranslate();
  const { mutateAsync: schedulePostEventEmail } = trpc.schedulePostEventEmail.useMutation();
  const { mutateAsync: unschedulePostEventEmail } = trpc.unschedulePostEventEmail.useMutation();

  const currentOffset = formData?.postEventSendDateOffset;

  const handleSubmit = React.useCallback(
    async (inDays: number) => {
      const { error } = await schedulePostEventEmail({ ticketingId: formId, offset: inDays });
      if (error) {
        await handleScheduleError();
      } else {
        logScheduleEvent({ offsetInDays: inDays, emailContentId });
        await handleScheduleSuccess();
      }
    },
    [formId, schedulePostEventEmail, emailContentId, logScheduleEvent, handleScheduleSuccess, handleScheduleError]
  );

  const handleCancel = React.useCallback(async () => {
    if (currentOffset) {
      const { error } = await unschedulePostEventEmail({ ticketingId: formId });
      if (error) {
        await handleUnscheduleError();
      } else {
        logUnscheduleEvent({ emailContentId });
        await handleUnscheduleSuccess();
      }
    } else {
      // Close the scheduler when cancelling a new (not yet scheduled) email
      await closeEmailScheduler(false);
    }
  }, [
    currentOffset,
    emailContentId,
    formId,
    unschedulePostEventEmail,
    logUnscheduleEvent,
    handleUnscheduleSuccess,
    handleUnscheduleError,
    closeEmailScheduler,
  ]);

  return (
    <DurationEmailScheduler
      isOpen={isOpen}
      title={t("dashboard", "EmailEditor.scheduler.title.post-event")}
      label={t("dashboard", "EmailEditor.scheduler.label.post-event")}
      scheduleInDays={currentOffset ?? 1}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onClose={async () => await closeEmailScheduler(false)}
      cancelLabel={
        currentOffset ? t("dashboard", "EmailScheduler.unschedule") : t("dashboard", "EmailScheduler.cancel")
      }
    />
  );
};
